<template>
	<div class="chart-wrapper">
		<div id="tv_chart_container" class="chart-box"></div>
	</div>
</template>

<script>
import {
	createChart
} from "lightweight-charts";
import io from "socket.io-client";
import { socketUrl } from "@/config.js";
import {
	get
} from '@/http/request'

export default {
	name: "KlineChart",
	props: {
		period: {
			type: String,
			default: ''
		},
		symbol: {
			type: String,
			default: ''
		},
		price: {
			type: [String, Number],
			default: 0
		},
		high: {
			type: [String, Number],
			default: 0
		},
		low: {
			type: [String, Number],
			default: 0
		},
		chartHeight: {
			type: Number,
			default: 400
		},
	},
	data() {
		return {
			// === 图表实例相关 ===
			chart: null, // 主图表
			candleSeries: null, // 蜡烛图
			volumeSeries: null, // 成交量柱状图
			// 均线图配置
			maLineList: [5, 10, 30], // MA天数
			maLineColor: ['#f0ad4e', '#3da5f4', '#c044e7'], // MA颜色
			candleMASeriesList: [], // 均线图 (MA)
			volMASeriesList: [], // 成交量均线图
			// === 数据相关 ===
			dataList: [], // 蜡烛图数据
			volumeData: [], // 成交量数据
			kLineList: [], // 接口获取的原始 k 线数据
			// === WebSocket ===
			socket: null, // socket.io 实例
			// === 开关 ===
			showMaChart: true, // 是否显示 MA 均线
		};
	},
	mounted() {
		this.startSocket()
		this.initChart()
	},
	beforeDestroy() {
		this.removeChart()
		if (this.socket) {
			this.socket.close();
			this.socket = null;
		}
	},
	watch: {
		period() {
			this.openKline()
		}
	},
	methods: {
		openKline() {
			this.getProductKline()
		},
		getProductKline(showLoading = false) {
			get(this.$apis.getProductKline, {
				code: this.symbol,
				period: this.period,
				showLoading,
			}).then(res => {
				if (res.code == 1) {
					if (res.code === 1) {
						this.kLineList = res.data
						this.dataList.splice(0, this.dataList.length);
						this.volumeData.splice(0, this.volumeData.length);
						this.kLineList.forEach((item) => {
							this.volumeData.push({
								time: item.time,
								value: Math.max(0, Math.log10(item.volume + 1)),
								color: item.close >= item.open ?
									'rgba(79, 255, 151, 0.3)' : 'rgba(255, 73, 118, 0.3)',
							})
							this.dataList.push({
								time: Number(item.time), // 秒级
								open: parseFloat(item.open),
								high: parseFloat(item.high),
								low: parseFloat(item.low),
								close: parseFloat(item.close),
								volume: parseFloat(item.volume),
							})
						});
						this.$nextTick(() => {
							if (this.dataList.length === 0) {
								return false
							}
							let lastItem = this.dataList[this.dataList.length - 1]
							let form = {
								close: lastItem.close,
								isUp: lastItem.close >
									lastItem.open
							}
							this.$emit('close', form)
							this.close = lastItem.close
							this.setData()
						})
					}
				}
			})
		},
		startSocket() {
			if (this.socket) {
				this.socket.close()
			}
			this.socket = io(socketUrl, {
				path: "/wss",
				transports: ["websocket"]
			});

			this.socket.on("connect", () => {
				console.log("✅ 已连接", this.socket.id);
				// 向服务端订阅频道（根据你们服务端约定）
				this.socket.emit("subscribe", "kline");
				this.socket.emit("subscribe", "depth");
				this.socket.emit("subscribe", "trade");
				this.socket.emit("subscribe", "market");
			});

			this.socket.on("kline", (msg) => {
				if (this.symbol == msg.code && this.period == msg.period) {
					//console.log("📩 收到 kline 数据:", msg);
					this.updateRealtimeCandle(msg);
				}
			});
			this.socket.on("trade", (msg) => {
				if (this.symbol == msg.code) {
					// console.log("📩 收到 trade 数据:", msg);
				}
			});
			this.socket.on("depth", (msg) => {
				if (this.symbol == msg.code) {
					// console.log("📩 收到 depth 数据:", msg);
				}
			});
			this.socket.on("market", (msg) => {
				if (this.symbol == msg.code) {
					console.log("📩 收到 market 数据:", msg);
					this.$emit("market-update", msg);
				}
			});

			this.socket.on("disconnect", () => {
				console.log("❌ 已断开");
			});
		},
		removeChart() {
			if (this.chart) {
				this.chart.remove()
			}
		},
		initChart() {
			this.removeChart()

			const chartContainer = document.getElementById("tv_chart_container");

			this.chart = createChart(chartContainer, {
				width: chartContainer.clientWidth,
				height: this.chartHeight,
				handleScroll: {
					mouseWheel: false,         // 禁止鼠标滚轮上下滚动
					pressedMouseMove: true,    // 开启拖动
					horzTouchDrag: true,       // 允许横向拖动
					vertTouchDrag: false       // 禁止纵向拖动
				},
				layout: {
					background: {
						color: '#000000'
					},
					textColor: '#d1d4dc',
				},
				grid: {
					vertLines: {
						color: "#222"
					},
					horzLines: {
						color: "#222"
					},
				},
				crosshair: {
					mode: 0,
					vertLine: {
						labelVisible: true
					},
				},
				localization: {
					timeFormatter: (time) => {
						const date = new Date(time * 1000); // 注意：time 是秒级
						const Y = date.getFullYear();
						const M = String(date.getMonth() + 1).padStart(2, '0'); // 月份 +1
						const D = String(date.getDate()).padStart(2, '0');
						const h = String(date.getHours()).padStart(2, '0');
						const m = String(date.getMinutes()).padStart(2, '0');
						const s = String(date.getSeconds()).padStart(2, '0');
						return `${Y}-${M}-${D} ${h}:${m}:${s}`; // 年月日时分秒格式
					},
				},
				rightPriceScale: {
					borderColor: "#555"
				},
				timeScale: {
					borderColor: '#555',
					timeVisible: true,
					tickMarkFormatter: (time) => {
						// time 是 Unix 时间戳（秒）
						const date = new Date(time * 1000); // 转成毫秒
						const h = String(date.getHours()).padStart(2, '0');
						const m = String(date.getMinutes()).padStart(2, '0');
						return `${h}:${m}`; // 自定义显示格式
					}
				},
			});

			//点击显示十字线
			const handleTap = (clientX, clientY) => {
				const rect = chartContainer.getBoundingClientRect();
				const x = clientX - rect.left;
				const y = clientY - rect.top;

				const time = this.chart.timeScale().coordinateToTime(x);
				const price = this.candleSeries.coordinateToPrice(y);

				if (time == null || price == null) return;

				this.chart.setCrosshairPosition(price, time, this.candleSeries);
			};

			chartContainer.addEventListener('click', (e) => handleTap(e.clientX, e.clientY));
			chartContainer.addEventListener('touchend', (e) => {
				const touch = e.changedTouches[0];
				handleTap(touch.clientX, touch.clientY);
			});

			// 蜡烛图
			this.candleSeries = this.chart.addCandlestickSeries({
				upColor: '#4fff97',
				downColor: '#ff4976',
				borderUpColor: '#4fff97',
				borderDownColor: '#ff4976',
				wickUpColor: '#4fff97',
				wickDownColor: '#ff4976',
			});

			// 蜡烛图主图
			this.candleSeries.priceScale().applyOptions({
				scaleMargins: {
					top: 0.05,
					bottom: 0.25
				}
			});

			// 添加成交量柱状图
			this.volumeSeries = this.chart.addHistogramSeries({
				priceScaleId: '', // 独立坐标
				lastValueVisible: false, // 不显示右侧价格 
				priceLineVisible: false, // 不显示横线
			});

			// 成交量图
			this.volumeSeries.priceScale().applyOptions({
				scaleMargins: {
					top: 0.8,
					bottom: 0,
				},
				borderVisible: false
			});

			this.initMaChart()

			// 窗口缩放自适应
			window.addEventListener("resize", () => {
				this.chart.applyOptions({
					width: window.innerWidth - 20
				});
			});
		},
		setData() {
			if (!this.dataList.length || !this.volumeData) return;
			// 设置数据
			this.candleSeries.setData(this.dataList);
			this.volumeSeries.setData(this.volumeData);

			if (this.showMaChart) {
				this.updateMAWatermark()
			}
		},
		initMaChart() {
			if (!this.showMaChart) return;
			// 清空之前的 series
			this.candleMASeriesList = [];
			this.volMASeriesList = [];
			// 创建主图 MA
			this.maLineList.forEach((day, index) => {
				const series = this.chart.addLineSeries({
					color: this.maLineColor[index] || '#ffffff',
					lineWidth: 1,
					lastValueVisible: false,
					priceLineVisible: false,
				});
				this.candleMASeriesList.push(series);
			});

			// 创建成交量 MA
			this.maLineList.forEach((day, index) => {
				const series = this.chart.addLineSeries({
					priceScaleId: '', // 绑定成交量轴
					color: this.maLineColor[index] || '#ffffff',
					lineWidth: 1,
					lastValueVisible: false,
					priceLineVisible: false,
				});
				this.volMASeriesList.push(series);
			});
		},
		updateMAWatermark() {
			// 主图 MA
			this.candleMASeriesList.forEach((series, index) => {
				const maData = this.calcMA(this.dataList, this.maLineList[index]);
				series.setData(maData);
			});
			this.volMASeriesList.forEach((series, index) => {
				const maData = this.calcMA(this.volumeData, this.maLineList[index]);
				series.setData(maData);
			});

			// 更新 watermark（显示最后一个 MA 值）
			const lastValues = this.maLineList.map((day) => {
				const maData = this.calcMA(this.dataList, day);
				return Number(maData[maData.length - 1]?.value) || 0;
			});

			const watermarkText = this.maLineList.map((day, index) => `MA${day}: ${lastValues[index].toFixed(2)}`)
				.join(' | ');

			this.chart.applyOptions({
				watermark: {
					visible: true,
					fontSize: 12,
					color: '#ffffff',
					horzAlign: 'left',
					vertAlign: 'top',
					text: watermarkText
				}
			});
		},
		updateRealtimeCandle(newPriceData) {
			// 最后一根 k 线下标
			const lastBarIndex = this.dataList.length - 1;
			// 最后一根 K 线数据
			const lastBar = this.dataList[lastBarIndex];

			// 统一格式化数据
			const candle = {
				time: Number(newPriceData.time),
				open: parseFloat(newPriceData.open),
				high: parseFloat(newPriceData.high),
				low: parseFloat(newPriceData.low),
				close: parseFloat(newPriceData.close),
				volume: parseFloat(newPriceData.volume),
			};

			if (candle.time === lastBar.time) {
				// 更新最后一根 K 线
				Object.assign(lastBar, candle);
				this.candleSeries.update(lastBar);

				// 更新交易图
				this.volumeSeries.update({
					time: candle.time,
					value: Math.max(0, Math.log10(candle.volume + 1)),
					color: candle.close >= candle.open ? "#4fff97" : "#ff4976",
				});
			} else if (candle.time > lastBar.time) {

				// 新增一根 K 线
				this.dataList.push(candle);
				this.candleSeries.update(candle);

				// 新增交易图
				this.volumeData.push({
					time: candle.time,
					value: Math.max(0, Math.log10(candle.volume + 1)),
					color: candle.close >= candle.open ? "#4fff97" : "#ff4976",
				})
				this.volumeSeries.update({
					time: candle.time,
					value: Math.max(0, Math.log10(candle.volume + 1)),
					color: candle.close >= candle.open ? "#4fff97" : "#ff4976",
				});
			}
			if (this.showMaChart) {
				this.updateMAWatermark();
			}
		},
		// 计算 MA
		calcMA(data, dayCount) {
			const result = [];
			for (let i = 0; i < data.length; i++) {
				if (i < dayCount) {
					result.push({
						time: data[i].time,
						value: Number
					});
					continue;
				}
				let sum = 0;
				for (let j = 0; j < dayCount; j++) {
					const val = data[i - j].close !== undefined ?
						data[i - j].close :
						data[i - j].value;
					sum += val;
				}
				result.push({
					time: data[i].time,
					value: sum / dayCount
				});
			}
			return result;
		}
	},
};
</script>

<style scoped>
.chart-wrapper {
	display: flex;
	justify-content: center;
	align-items: center;
}

.chart-box {
	width: 100%;
	height: 400px;
}
</style>